import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Admin-side state for the User Feedback inbox (Luciq replacement). The backend
// returns a plain array capped at the newest N reports (see the bounded `limit`
// in routes/userFeedback.js) — no page cursor, since the inbox merges the
// latest-N from several independent nodes rather than paging a mixed set.
//
// For an @inovo admin the inbox spans nodes, and report ids are only unique
// within a node. Every row carries `originNode`; each single-thread call must
// send it back or the backend resolves the id against the wrong node's DB.

export const readUserFeedbackAsync = createAsyncThunk(
  "userFeedback/readMany",
  async ({ status } = {}) => {
    const params = status && status !== "all" ? { status } : {};
    const response = await axios.get("/api/admin/user-feedback", { params });
    return response.data;
  }
);

export const readUserFeedbackThreadAsync = createAsyncThunk(
  "userFeedback/readThread",
  async ({ id, originNode }) => {
    const params = originNode ? { originNode } : {};
    const response = await axios.get(`/api/user-feedback/${id}`, { params });
    return response.data;
  }
);

export const postFeedbackReplyAsync = createAsyncThunk(
  "userFeedback/reply",
  async ({ id, message, originNode, attachment }, { dispatch }) => {
    if (attachment) {
      const form = new FormData();
      form.append("message", message);
      if (originNode) form.append("originNode", originNode);
      form.append("attachment", attachment);
      await axios.post(`/api/user-feedback/${id}/messages`, form);
    } else {
      await axios.post(`/api/user-feedback/${id}/messages`, { message, originNode });
    }
    dispatch(readUserFeedbackThreadAsync({ id, originNode }));
    return true;
  }
);

export const updateFeedbackStatusAsync = createAsyncThunk(
  "userFeedback/updateStatus",
  async ({ id, status, originNode }) => {
    const response = await axios.patch(`/api/user-feedback/${id}/status`, {
      status,
      originNode
    });
    return response.data;
  }
);

const initialState = {
  loading: false,
  error: null,
  reports: [],
  statusFilter: "open",
  activeThread: null,
  threadLoading: false,
  threadLoaded: false,
  submitting: false
};

const userFeedbackSlice = createSlice({
  name: "userFeedback",
  initialState,
  reducers: {
    setStatusFilter(state, action) {
      state.statusFilter = action.payload;
    },
    clearActiveThread(state) {
      state.activeThread = null;
      state.threadLoaded = false;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readUserFeedbackAsync.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(readUserFeedbackAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(readUserFeedbackAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to load feedback";
      })
      .addCase(readUserFeedbackThreadAsync.pending, state => {
        state.threadLoading = true;
        state.threadLoaded = false;
      })
      .addCase(readUserFeedbackThreadAsync.fulfilled, (state, action) => {
        state.threadLoading = false;
        state.threadLoaded = true;
        state.activeThread = action.payload;
      })
      .addCase(readUserFeedbackThreadAsync.rejected, state => {
        state.threadLoading = false;
        state.threadLoaded = true;
      })
      .addCase(postFeedbackReplyAsync.pending, state => {
        state.submitting = true;
      })
      .addCase(postFeedbackReplyAsync.fulfilled, state => {
        state.submitting = false;
      })
      .addCase(postFeedbackReplyAsync.rejected, state => {
        state.submitting = false;
      })
      .addCase(updateFeedbackStatusAsync.fulfilled, (state, action) => {
        if (state.activeThread && action.payload) {
          state.activeThread = { ...state.activeThread, status: action.payload.status };
        }
      });
  }
});

export const { setStatusFilter, clearActiveThread } = userFeedbackSlice.actions;
export default userFeedbackSlice.reducer;
